import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { notification } from "antd";
import {
  getLocaleForLanguage,
  normalizeLanguage,
} from "../../services/language";
import { buildPlayerHistoryRoute } from "./leaderboardProfileNavigation";
import ModernSidebar, { useModernRouteActions } from "../../components/lobby/ModernSidebar";
import PuzzleBoard2D from "../../components/puzzles/PuzzleBoard2D";
import "../../components/lobby/lobbyModern.css";
import "./puzzlesV2.css";
import "./spectateV2.css";

// «Ver partidos» con el diseño Clásico (estilo 2, elegido por Yosdel el 2026-10-17): filtros a la
// izquierda, partido destacado (el más visto) y listas con el tablero 2D visto desde arriba.
// Datos reales de /tournaments/live (se refresca cada 5 s).

const SPECTATE_COPY = Object.freeze({
  lobby: { es: "Lobby", en: "Lobby", de: "Lobby", ru: "Лобби" },
  watchMatches: { es: "Ver Partidos", en: "Watch Matches", de: "Partien ansehen", ru: "Смотреть партии" },
  heroDescription: {
    es: "Mira partidas en vivo como espectador o aprende con las mejores repeticiones.",
    en: "Watch live matches as a spectator or learn from the best replays.",
    de: "Live-Partien, auch Spieler gegen Bots. Tritt als Zuschauer bei.",
    ru: "Партии в прямом эфире, включая игры с ботами. Войди как зритель.",
  },
  refresh: { es: "Actualizar", en: "Refresh", de: "Aktualisieren", ru: "Обновить" },
  refreshing: { es: "Actualizando...", en: "Refreshing...", de: "Aktualisieren...", ru: "Обновление..." },
  live: { es: "En vivo", en: "Live", de: "Live", ru: "В эфире" },
  connectedPlayers: {
    es: "Jugadores en partida",
    en: "Players in match",
    de: "Spieler in der Partie",
    ru: "Игроки в партии",
  },
  spectators: { es: "Espectadores", en: "Spectators", de: "Zuschauer", ru: "Зрители" },
  replays: { es: "Replays", en: "Replays", de: "Wiederholungen", ru: "Повторы" },
  onlineRooms: { es: "Salas online", en: "Online rooms", de: "Online-Räume", ru: "Онлайн-комнаты" },
  liveMatches: { es: "Partidos en vivo", en: "Live matches", de: "Live-Partien", ru: "Партии в прямом эфире" },
  noLiveMatches: {
    es: "No hay partidas activas ahora mismo.",
    en: "There are no active matches right now.",
    de: "Derzeit gibt es keine aktiven Partien.",
    ru: "Сейчас нет активных партий.",
  },
  bestHistoricalMatches: {
    es: "Mejores partidas históricas",
    en: "Best historic matches",
    de: "Beste historische Partien",
    ru: "Лучшие исторические партии",
  },
  featuredReplays: {
    es: "Repeticiones destacadas",
    en: "Featured replays",
    de: "Empfohlene Wiederholungen",
    ru: "Рекомендуемые повторы",
  },
  bestHistoricalSubtitle: {
    es: "Si no hay partidas en vivo, aquí siempre queda una buena partida para aprender.",
    en: "If there are no live matches, there is always a good match here to learn from.",
    de: "Wenn es keine Live-Partien gibt, findest du hier immer eine gute Partie zum Lernen.",
    ru: "Если сейчас нет партий в прямом эфире, здесь всегда есть хорошая партия для обучения.",
  },
  bestHumanMatchByLevel: {
    es: "Mejor partida humana por nivel",
    en: "Best human match by level",
    de: "Beste menschliche Partie nach Stufe",
    ru: "Лучшая партия человека по уровню",
  },
  featuredHumanMatch: {
    es: "Partida humana destacada",
    en: "Featured human match",
    de: "Empfohlene menschliche Partie",
    ru: "Рекомендуемая партия человека",
  },
  humanVsBotMatch: {
    es: "Partida humano vs bot",
    en: "Human vs bot match",
    de: "Mensch gegen Bot Partie",
    ru: "Партия человек против бота",
  },
  featuredMatch: { es: "Partida destacada", en: "Featured match", de: "Empfohlene Partie", ru: "Рекомендуемая партия" },
  challengeCreatedBy: {
    es: "Desafío creado por {name}",
    en: "Challenge created by {name}",
    de: "Herausforderung erstellt von {name}",
    ru: "Вызов создан {name}",
  },
  cimaxModeScore: {
    es: "CIMAX {mode}: {score}",
    en: "CIMAX {mode}: {score}",
    de: "CIMAX {mode}: {score}",
    ru: "CIMAX {mode}: {score}",
  },
  twoPlayerGameWithDiceChoice: {
    es: "Partida de 2 jugadores: elige moverte con o sin dado. 10 minutos.",
    en: "Two-player game: choose to move with or without dice. 10 minutes.",
    de: "Partie für 2 Spieler: Wähle, ob du mit oder ohne Würfel ziehen möchtest. 10 Minuten.",
    ru: "Партия для 2 игроков: выбери, делать ходы с кубиком или без него. 10 минут.",
  },
  onlineMatch: { es: "Partida Online", en: "Online Match", de: "Online-Partie", ru: "Онлайн-партия" },
  historicalMatch: { es: "Partida histórica", en: "Historical Match", de: "Historische Partie", ru: "Историческая партия" },
  mode: { es: "Modo", en: "Mode", de: "Modus", ru: "Режим" },
  time: { es: "Tiempo", en: "Time", de: "Zeit", ru: "Время" },
  players: { es: "Jugadores", en: "Players", de: "Spieler", ru: "Игроки" },
  level: { es: "Nivel", en: "Level", de: "Stufe", ru: "Уровень" },
  pending: { es: "por registrar", en: "pending", de: "ausstehend", ru: "ожидается" },
  replay: { es: "Repetición", en: "Replay", de: "Wiederholung", ru: "Повтор" },
  watchReplay: { es: "Ver repetición", en: "Watch replay", de: "Wiederholung ansehen", ru: "Смотреть повтор" },
  watchLive: { es: "Ver en vivo", en: "Watch live", de: "Live ansehen", ru: "Смотреть в эфире" },
  joining: { es: "Entrando...", en: "Joining...", de: "Beitreten...", ru: "Вход..." },
  noPlayerData: {
    es: "Sin datos de jugadores",
    en: "No player data",
    de: "Keine Spielerdaten",
    ru: "Нет данных об игроках",
  },
  errorTitle: { es: "Error", en: "Error", de: "Fehler", ru: "Ошибка" },
  liveMatchesLoadError: {
    es: "No se pudieron cargar los partidos en vivo.",
    en: "Could not load live matches.",
    de: "Live-Partien konnten nicht geladen werden.",
    ru: "Не удалось загрузить партии в прямом эфире.",
  },
  botSuffix: { es: "BOT", en: "BOT", de: "BOT", ru: "BOT" },
  science: { es: "Ciencia", en: "Science", de: "Wissenschaft", ru: "Наука" },
  chance: { es: "Azar", en: "Chance", de: "Zufall", ru: "Случайность" },
  cienzar: { es: "CIENZAR", en: "CIENZAR", de: "CIENZAR", ru: "CIENZAR" },
  scienceVsAzar: {
    es: "Contrazar",
    en: "Contrazar",
    de: "Contrazar",
    ru: "Contrazar",
  },
  // Página rediseñada (estilo 2, 2026-10-17).
  backToLobby: { es: "Volver al Lobby", en: "Back to Lobby" },
  liveUpper: { es: "EN VIVO", en: "LIVE" },
  liveN: { es: "{n} en vivo", en: "{n} live" },
  playingN: { es: "{n} jugando", en: "{n} playing" },
  watchingN: { es: "{n} mirando", en: "{n} watching" },
  turnN: { es: "Turno {n}", en: "Turn {n}" },
  movesN: { es: "{n} jugadas", en: "{n} moves" },
  moreN: { es: "{n} más", en: "{n} more" },
  replayMoves: { es: "Repetición · {n} jugadas", en: "Replay · {n} moves" },
  viewLabel: { es: "Ver", en: "View" },
  viewAll: { es: "Todo", en: "All" },
  cienzarName: { es: "Cienzar", en: "Cienzar" },
  replaysFilter: { es: "Repeticiones", en: "Replays" },
  featuredGame: { es: "Partido destacado", en: "Featured match" },
  mostWatched: { es: "La partida más vista ahora", en: "The most watched match right now" },
  enterAsSpectator: { es: "Entrar como espectador", en: "Join as spectator" },
  enterToWatch: { es: "Entrar a ver", en: "Watch" },
  nobodyPlaying: { es: "Ahora mismo nadie está jugando", en: "Nobody is playing right now" },
  nobodyPlayingReplay: {
    es: "Mientras tanto, mira la mejor partida guardada y aprende jugada a jugada.",
    en: "Meanwhile, watch the best saved match and learn move by move.",
  },
  nobodyPlayingLater: {
    es: "Vuelve en un rato: las partidas aparecen aquí en cuanto empiezan.",
    en: "Come back soon: matches show up here as soon as they start.",
  },
  watchBestReplay: { es: "Ver la mejor repetición", en: "Watch the best replay" },
  bestPerMode: { es: "la mejor partida de cada modo", en: "the best match of each mode" },
  howItWorks: { es: "¿Cómo funciona?", en: "How does it work?" },
  howItWorksText: {
    es: "Entra a cualquier partida en vivo sin molestar a los jugadores. Las repeticiones se pueden pausar y ver jugada a jugada.",
    en: "Join any live match without disturbing the players. Replays can be paused and watched move by move.",
  },
  noReplays: { es: "Todavía no hay repeticiones destacadas.", en: "There are no featured replays yet." },
  noReplaysMode: { es: "No hay repeticiones de {mode} todavía.", en: "No {mode} replays yet." },
  boardLabel: { es: "Tablero de la partida visto desde arriba", en: "Match board seen from above" },
  finalBoardLabel: { es: "Posición final vista desde arriba", en: "Final position seen from above" },
  openPlayerHistory: { es: "Ver historial del jugador", en: "Open player history" },
});

const getSpectateCopy = (language, key, values = {}) => {
  const normalizedLanguage = normalizeLanguage(language);
  const entry = SPECTATE_COPY[key] || {};
  let copy = entry[normalizedLanguage] || entry.es || entry.en || "";

  Object.entries(values || {}).forEach(([name, value]) => {
    copy = copy.split(`{${name}}`).join(String(value));
  });

  return copy;
};

const formatSpectateNumber = (value, language) =>
  new Intl.NumberFormat(getLocaleForLanguage(language), {
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(Number(value) || 0)));

const normalizeDisplayModeKey = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  if (normalized === "free" || normalized === "botlocal" || normalized === "botlocalfree") {
    return "science";
  }
  if (normalized === "dice" || normalized === "botlocaldice") return "chance";
  if (
    [
      "sciencevsazar",
      "science-vs-azar",
      "science vs azar",
      "science v/s azar",
      "science vs chance",
      "science v/s chance",
      "cienciavsazar",
      "ciencia-vs-azar",
      "ciencia vs azar",
      "ciencia v/s azar",
      "contrazar",
      "localsciencevsazar",
      "botlocalsciencevsazar",
    ].includes(normalized)
  ) {
    return "scienceVsAzar";
  }

  if (["ciencia", "science", "wissenschaft", "наука"].includes(normalized)) {
    return "science";
  }
  if (["azar", "chance", "zufall", "случайность", "random", "free"].includes(normalized)) {
    return "chance";
  }
  if (["cienzar", "dice", "botlocalcienzar"].includes(normalized)) {
    return "cienzar";
  }
  if (
    [
      "sciencevsazar",
      "science-vs-azar",
      "cienciavsazar",
      "ciencia-vs-azar",
      "localsciencevsazar",
      "botlocalsciencevsazar",
    ].includes(normalized)
  ) {
    return "scienceVsAzar";
  }
  return "";
};

const getModeCopyKey = (mode) => {
  const normalized = String(mode || "").trim().toLowerCase();
  if (normalized === "free" || normalized === "botlocal" || normalized === "botlocalfree") {
    return "science";
  }
  if (normalized === "dice" || normalized === "botlocaldice") return "chance";
  if (normalized === "cienzar" || normalized === "botlocalcienzar") return "cienzar";
  if (
    normalized === "sciencevsazar" ||
    normalized === "science-vs-azar" ||
    normalized === "science vs azar" ||
    normalized === "science v/s azar" ||
    normalized === "science vs chance" ||
    normalized === "science v/s chance" ||
    normalized === "cienciavsazar" ||
    normalized === "ciencia-vs-azar" ||
    normalized === "ciencia vs azar" ||
    normalized === "ciencia v/s azar" ||
    normalized === "contrazar" ||
    normalized === "localsciencevsazar" ||
    normalized === "botlocalsciencevsazar"
  ) {
    return "scienceVsAzar";
  }
  return normalizeDisplayModeKey(mode);
};

const formatMode = (mode, language = "es") => {
  const copyKey = getModeCopyKey(mode);
  if (copyKey) return getSpectateCopy(language, copyKey);
  return String(mode || "").trim().toUpperCase();
};

const localizeModeLabel = (value, language = "es") => {
  const copyKey = normalizeDisplayModeKey(value) || getModeCopyKey(value);
  if (copyKey) return getSpectateCopy(language, copyKey);
  return String(value || "").trim().toUpperCase();
};

const formatHistoricalKindLabel = (kind, language = "es") => {
  const normalizedKind = String(kind || "").trim();
  if (normalizedKind === "best_human_duel") {
    return getSpectateCopy(language, "bestHumanMatchByLevel");
  }
  if (normalizedKind === "human_duel") {
    return getSpectateCopy(language, "featuredHumanMatch");
  }
  if (normalizedKind === "human_bot_duel") {
    return getSpectateCopy(language, "humanVsBotMatch");
  }
  return getSpectateCopy(language, "featuredMatch");
};

const normalizePlayerCode = (value) => {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (!raw) return "";
  if (/^\d{4}$/.test(raw)) return `XIMA-${raw}`;
  return raw;
};

const resolveLivePlayerProfileTarget = (player) => {
  if (player && typeof player === "object") {
    const byCode = normalizePlayerCode(player?.playerCode);
    if (byCode) return byCode;
    const byLogin = String(player?.loginIdentifier || player?.email || "").trim();
    if (byLogin) return byLogin;
    const byId = String(player?.userId || player?._id || player?.id || "").trim();
    if (byId) return byId;
    return String(player?.displayName || player?.name || "").trim();
  }
  return String(player || "").trim();
};

const resolveLivePlayerLabel = (player) => {
  if (player && typeof player === "object") {
    return String(
      player?.displayName ||
        player?.name ||
        player?.playerCode ||
        player?.email ||
        player?.userId ||
        player?._id ||
        "-"
    ).trim();
  }
  return String(player || "").trim() || "-";
};

const formatHistoricalDate = (value, language = "es") => {
  const date = new Date(value || 0);
  if (!Number.isFinite(date.valueOf()) || date.valueOf() <= 0) return "";
  const formattedDate = new Intl.DateTimeFormat(getLocaleForLanguage(language), {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);

  if (normalizeLanguage(language) === "ru") {
    return formattedDate.replace(/\s?г\.$/i, "");
  }

  return formattedDate;
};

const getSpectateModeTone = (mode) => {
  const normalized = String(mode || "").toLowerCase();
  if (
    normalized === "sciencevsazar" ||
    normalized === "localsciencevsazar" ||
    normalized === "botlocalsciencevsazar"
  ) {
    return "sciencevsazar";
  }
  if (["free", "botlocal", "botlocalfree"].includes(normalized)) return "ciencia";
  if (["dice", "botlocaldice"].includes(normalized)) return "azar";
  if (["cienzar", "botlocalcienzar"].includes(normalized)) return "cienzar";
  return "neutral";
};


// Filtro de modo de la página (Ciencia / Cienzar / Contrazar / Azar) a partir del modo de la sala.
const MODE_FILTERS = [
  { id: "ciencia", icon: "🔷", copyKey: "science" },
  { id: "cienzar", icon: "🟪", copyKey: "cienzarName" },
  { id: "contrazar", icon: "🟩", copyKey: "scienceVsAzar" },
  { id: "azar", icon: "🟥", copyKey: "chance" },
];

const VIEW_FILTERS = [
  { id: "all", icon: "✨", copyKey: "viewAll" },
  { id: "live", icon: "🔴", copyKey: "live" },
  { id: "replays", icon: "🎬", copyKey: "replaysFilter" },
];

const getModeFilterId = (mode) => {
  const tone = getSpectateModeTone(mode);
  if (tone === "sciencevsazar") return "contrazar";
  if (tone !== "neutral") return tone;
  const key = normalizeDisplayModeKey(mode);
  if (key === "science") return "ciencia";
  if (key === "chance") return "azar";
  if (key === "scienceVsAzar") return "contrazar";
  return key || "";
};

const formatClock = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil((Number(ms) || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const getPlayers = (match) => (Array.isArray(match?.players) ? match.players : []);

const SpectateMatches = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext() || {};
  const routeActions = useModernRouteActions();
  const language = normalizeLanguage(outletContext.language || "es");
  const isAdmin = Boolean(outletContext.hasAdminMenuAccess);
  const ui = useCallback(
    (key, values) => getSpectateCopy(language, key, values),
    [language]
  );
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [joiningRoomId, setJoiningRoomId] = useState("");
  const [fetchedAt, setFetchedAt] = useState(() => Date.now());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [view, setView] = useState("all");
  const [modeFilter, setModeFilter] = useState("");

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  useEffect(() => {
    document.body.classList.add("xima-lobby-modern-active", "xsp-page-active");
    return () => document.body.classList.remove("xima-lobby-modern-active", "xsp-page-active");
  }, []);

  const loadMatches = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/tournaments/live");
      setMatches(Array.isArray(response?.data) ? response.data : []);
      setFetchedAt(Date.now());
    } catch (error) {
      console.error(error);
      notification.error({
        message: ui("errorTitle"),
        description:
          error?.response?.data?.message ||
          error?.message ||
          ui("liveMatchesLoadError"),
      });
    } finally {
      setLoading(false);
    }
  }, [ui]);

  useEffect(() => {
    loadMatches();
    const intervalId = window.setInterval(loadMatches, 5000);
    return () => window.clearInterval(intervalId);
  }, [loadMatches]);

  // Reloj del turno: el servidor da el tiempo que quedaba al pedir la lista; aquí se descuenta.
  useEffect(() => {
    const tickId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(tickId);
  }, []);

  const liveMatches = useMemo(
    () => matches.filter((match) => String(match?.type || "live").toLowerCase() !== "historical"),
    [matches]
  );
  const historicalMatches = useMemo(
    () => matches.filter((match) => String(match?.type || "").toLowerCase() === "historical"),
    [matches]
  );
  const totalSpectators = useMemo(
    () =>
      liveMatches.reduce((sum, match) => sum + Math.max(0, Number(match?.spectators) || 0), 0),
    [liveMatches]
  );
  const totalConnectedPlayers = useMemo(
    () => liveMatches.reduce((sum, match) => sum + getPlayers(match).length, 0),
    [liveMatches]
  );

  const byMode = useCallback(
    (list) => (modeFilter ? list.filter((match) => getModeFilterId(match?.mode) === modeFilter) : list),
    [modeFilter]
  );
  const visibleLive = useMemo(() => byMode(liveMatches), [byMode, liveMatches]);
  const visibleReplays = useMemo(() => byMode(historicalMatches), [byMode, historicalMatches]);
  const showLive = view !== "replays";
  const showReplays = view !== "live";

  // Partido destacado: la partida en vivo con más espectadores.
  const featured = useMemo(() => {
    if (!visibleLive.length) return null;
    return visibleLive.reduce((best, match) =>
      (Number(match?.spectators) || 0) > (Number(best?.spectators) || 0) ? match : best
    );
  }, [visibleLive]);
  const otherLive = useMemo(
    () => visibleLive.filter((match) => match !== featured),
    [visibleLive, featured]
  );
  const bestReplay = useMemo(() => {
    const pool = (visibleReplays.length ? visibleReplays : historicalMatches).filter(
      (match) => match?.hasReplay !== false
    );
    if (!pool.length) return null;
    return pool.reduce((best, match) =>
      (Number(match?.levelGame) || 0) > (Number(best?.levelGame) || 0) ? match : best
    );
  }, [visibleReplays, historicalMatches]);

  const viewCounts = {
    all: visibleLive.length + visibleReplays.length,
    live: visibleLive.length,
    replays: visibleReplays.length,
  };
  const modeCounts = useMemo(() => {
    const counts = {};
    const pool = view === "live" ? liveMatches : view === "replays" ? historicalMatches : matches;
    pool.forEach((match) => {
      const id = getModeFilterId(match?.mode);
      if (id) counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [view, liveMatches, historicalMatches, matches]);

  const handleWatchMatch = useCallback(
    (match) => {
      const tournamentId = String(match?.tournamentId || "");
      const roomId = String(match?.roomId || "");
      if (!tournamentId || !roomId) return;

      setJoiningRoomId(roomId);
      navigate(
        `/game/${tournamentId}?autoplay=1&spectator=1&roomId=${encodeURIComponent(roomId)}`
      );
    },
    [navigate]
  );
  const handleWatchHistoricalMatch = useCallback(
    (match) => {
      const historyId = String(match?.historyId || match?.id || "").trim();
      if (!historyId || match?.hasReplay === false) return;
      navigate("/game/history", {
        state: {
          openReplayRequestKey: `watch-highlight:${historyId}:${Date.now()}`,
          openReplayMatch: {
            id: historyId,
            historyId,
            tournamentName: match?.tournamentName || "Partida historica",
            mode: match?.mode || "",
            modeLabel: match?.modeLabel || formatMode(match?.mode, language),
            hasReplay: true,
            replaySteps: Number(match?.replaySteps) || 0,
            participants: getPlayers(match),
          },
        },
      });
    },
    [language, navigate]
  );
  const handleOpenPlayerProfile = useCallback(
    (player) => {
      const target = resolveLivePlayerProfileTarget(player);
      if (!target) return;
      navigate(buildPlayerHistoryRoute(target));
    },
    [navigate]
  );

  const modeTone = (match) => getModeFilterId(match?.mode) || "neutral";
  const modeChip = (match) => (
    <span className={`xsp-mode is-${modeTone(match)}`}>
      {localizeModeLabel(match?.modeLabel || match?.mode, language) || formatMode(match?.mode, language)}
    </span>
  );
  const liveChip = (
    <span className="xsp-live">
      <i aria-hidden="true" />
      {ui("liveUpper")}
    </span>
  );
  const remainingFor = (match) =>
    Math.max(0, (Number(match?.turnRemainingMs) || 0) - Math.max(0, nowMs - fetchedAt));

  const playerName = (player, key) => {
    const label = resolveLivePlayerLabel(player);
    const isBot = Boolean(player?.isBot);
    return (
      <button
        type="button"
        key={key}
        className="xsp-name"
        disabled={isBot}
        onClick={() => {
          if (!isBot) handleOpenPlayerProfile(player);
        }}
        title={isBot ? undefined : ui("openPlayerHistory")}
      >
        {label}
        {isBot ? ` ${ui("botSuffix")}` : ""}
      </button>
    );
  };
  const versus = (match, prefix) => {
    const players = getPlayers(match);
    if (!players.length) return <span className="xsp-muted">{ui("noPlayerData")}</span>;
    return players.map((player, index) => (
      <React.Fragment key={`${prefix}-${index}`}>
        {index > 0 ? <span className="xsp-vs"> vs </span> : null}
        {playerName(player, `${prefix}-name-${index}`)}
      </React.Fragment>
    ));
  };
  const liveMeta = (match) => {
    const spectators = Number(match?.spectators) || 0;
    return (
      <>
        <span>
          ⏱ {ui("turnN", { n: formatSpectateNumber(match?.turnNumber || 1, language) })} ·{" "}
          {formatClock(remainingFor(match))}
        </span>
        <span>👁 {ui("watchingN", { n: formatSpectateNumber(spectators, language) })}</span>
        <span>♟ {ui("movesN", { n: formatSpectateNumber(match?.moveCount || 0, language) })}</span>
      </>
    );
  };

  const renderFeatured = () => {
    if (!featured) {
      return (
        <section className="xsp-featured is-empty">
          <div className="xsp-featured-empty-icon" aria-hidden="true">
            👀
          </div>
          <div className="xsp-featured-info">
            <span className="xsp-featured-label">{ui("liveMatches")}</span>
            <h2>{ui("nobodyPlaying")}</h2>
            <p>{bestReplay ? ui("nobodyPlayingReplay") : ui("nobodyPlayingLater")}</p>
            {bestReplay ? (
              <button
                type="button"
                className="xpz-btn is-gold is-large"
                onClick={() => handleWatchHistoricalMatch(bestReplay)}
              >
                ▶ {ui("watchBestReplay")}
              </button>
            ) : null}
          </div>
        </section>
      );
    }
    const players = getPlayers(featured);
    const roomId = String(featured?.roomId || "");
    return (
      <section className="xsp-featured">
        <div className="xsp-featured-board">
          <PuzzleBoard2D board={featured?.board2d} size={330} label={ui("boardLabel")} />
        </div>
        <div className="xsp-featured-info">
          <div className="xsp-chips">
            {liveChip}
            {modeChip(featured)}
          </div>
          <span className="xsp-featured-label">{ui("featuredGame")}</span>
          <h2>{ui("mostWatched")}</h2>
          <div className="xsp-duel">
            {players.slice(0, 4).map((player, index) => (
              <React.Fragment key={`featured-${index}`}>
                {index > 0 ? <span className="xsp-duel-vs">VS</span> : null}
                <div className={`xsp-duel-player is-p${index}`}>
                  <span className="xsp-avatar" aria-hidden="true">
                    {resolveLivePlayerLabel(player).charAt(0).toUpperCase()}
                  </span>
                  {playerName(player, `featured-name-${index}`)}
                </div>
              </React.Fragment>
            ))}
            {!players.length ? <span className="xsp-muted">{ui("noPlayerData")}</span> : null}
          </div>
          <div className="xsp-meta is-large">{liveMeta(featured)}</div>
          <button
            type="button"
            className="xpz-btn is-gold is-large xsp-enter"
            onClick={() => handleWatchMatch(featured)}
            disabled={joiningRoomId === roomId}
          >
            👁 {joiningRoomId === roomId ? ui("joining") : ui("enterAsSpectator")}
          </button>
        </div>
      </section>
    );
  };

  const renderLiveRow = (match) => {
    const roomId = String(match?.roomId || "");
    const tournamentId = String(match?.tournamentId || "");
    return (
      <article key={`live-${tournamentId}-${roomId}`} className={`xsp-row is-${modeTone(match)}`}>
        <PuzzleBoard2D board={match?.board2d} size={190} label={ui("boardLabel")} />
        <div className="xsp-row-info">
          <div className="xsp-chips">
            {modeChip(match)}
            {liveChip}
          </div>
          <div className="xsp-row-title">{versus(match, `live-${roomId}`)}</div>
          <div className="xsp-meta">{liveMeta(match)}</div>
        </div>
        <button
          type="button"
          className="xpz-btn is-gold"
          onClick={() => handleWatchMatch(match)}
          disabled={joiningRoomId === roomId}
        >
          {joiningRoomId === roomId ? ui("joining") : ui("enterToWatch")}
        </button>
      </article>
    );
  };

  const renderReplayRow = (match) => {
    const historyId = String(match?.historyId || match?.id || "");
    const levelGame = Number(match?.levelGame) || 0;
    const replaySteps = Number(match?.replaySteps) || 0;
    const endedAtLabel = formatHistoricalDate(match?.endedAt, language);
    return (
      <article key={`history-${historyId}`} className={`xsp-row is-${modeTone(match)}`}>
        <PuzzleBoard2D board={match?.board2d} size={190} label={ui("finalBoardLabel")} />
        <div className="xsp-row-info">
          <div className="xsp-chips">
            {modeChip(match)}
            <span className="xsp-kind">{formatHistoricalKindLabel(match?.historicalKind, language)}</span>
          </div>
          <div className="xsp-row-title">{versus(match, `history-${historyId}`)}</div>
          <div className="xsp-meta">
            <span>
              📈 {ui("level")} {levelGame > 0 ? formatSpectateNumber(levelGame, language) : ui("pending")}
            </span>
            <span>🎬 {ui("replayMoves", { n: formatSpectateNumber(replaySteps, language) })}</span>
            {endedAtLabel ? <span>📅 {endedAtLabel}</span> : null}
          </div>
        </div>
        <button
          type="button"
          className="xpz-btn is-ghost"
          onClick={() => handleWatchHistoricalMatch(match)}
          disabled={!match?.hasReplay}
        >
          ▶ {ui("watchReplay")}
        </button>
      </article>
    );
  };

  const modeName = (id) => {
    const entry = MODE_FILTERS.find((item) => item.id === id);
    return entry ? ui(entry.copyKey) : "";
  };

  return (
    <div className="xlm-root xpz-root xsp-root">
      <ModernSidebar
        language={language}
        active="watch"
        badges={outletContext.sidebarBadges || {}}
        isAdmin={isAdmin}
        actions={routeActions}
      />
      <div className="xlm-main xpz-main xsp-main">
        <div className="xpz-shell">
          <header className="xpz-top">
            <div className="xpz-title">
              <button type="button" className="xpz-back" onClick={() => navigate("/game/lobby")}>
                ‹ {ui("backToLobby")}
              </button>
              <h1>{ui("watchMatches")}</h1>
              <p>{ui("heroDescription")}</p>
            </div>
            <div className="xsp-top-pills">
              <span className="xsp-top-pill" title={ui("live")}>
                🔴 {ui("liveN", { n: formatSpectateNumber(liveMatches.length, language) })}
              </span>
              <span className="xsp-top-pill" title={ui("connectedPlayers")}>
                👥 {ui("playingN", { n: formatSpectateNumber(totalConnectedPlayers, language) })}
              </span>
              <span className="xsp-top-pill" title={ui("spectators")}>
                👁 {ui("watchingN", { n: formatSpectateNumber(totalSpectators, language) })}
              </span>
              <button
                type="button"
                className="xpz-btn is-ghost"
                onClick={loadMatches}
                disabled={loading}
              >
                ⟳ {loading ? ui("refreshing") : ui("refresh")}
              </button>
            </div>
          </header>

          <div className="xpz-layout xsp-layout">
            <aside className="xpz-side xsp-side">
              <div className="xpz-side-label">{ui("viewLabel")}</div>
              {VIEW_FILTERS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={`xpz-pill${view === entry.id ? " is-active" : ""}`}
                  onClick={() => setView(entry.id)}
                >
                  <span>
                    <i aria-hidden="true">{entry.icon}</i> {ui(entry.copyKey)}
                  </span>
                  <small>{formatSpectateNumber(viewCounts[entry.id], language)}</small>
                </button>
              ))}
              <div className="xpz-side-label">{ui("mode")}</div>
              {MODE_FILTERS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={`xpz-pill${modeFilter === entry.id ? " is-active" : ""}`}
                  onClick={() => setModeFilter((current) => (current === entry.id ? "" : entry.id))}
                >
                  <span>
                    <i aria-hidden="true">{entry.icon}</i> {ui(entry.copyKey)}
                  </span>
                  <small>{formatSpectateNumber(modeCounts[entry.id] || 0, language)}</small>
                </button>
              ))}
              <div className="xsp-help">
                <strong>👁 {ui("howItWorks")}</strong>
                <p>{ui("howItWorksText")}</p>
              </div>
            </aside>

            <div className="xsp-content">
              {showLive ? renderFeatured() : null}

              {showLive && otherLive.length > 0 ? (
                <section className="xsp-section">
                  <h3 className="xsp-section-title">
                    🔴 {ui("live")} <span>· {ui("moreN", { n: formatSpectateNumber(otherLive.length, language) })}</span>
                  </h3>
                  <div className="xsp-list">{otherLive.map(renderLiveRow)}</div>
                </section>
              ) : null}

              {showReplays ? (
                <section className="xsp-section">
                  <h3 className="xsp-section-title">
                    🏆 {ui("featuredReplays")} <span>· {ui("bestPerMode")}</span>
                  </h3>
                  {visibleReplays.length > 0 ? (
                    <div className="xsp-list">{visibleReplays.map(renderReplayRow)}</div>
                  ) : (
                    <div className="xpz-empty">
                      {modeFilter ? ui("noReplaysMode", { mode: modeName(modeFilter) }) : ui("noReplays")}
                    </div>
                  )}
                </section>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpectateMatches;
